import { ArrowRightOutlined } from "@ant-design/icons";
import { fmtEuro } from "../../../../../utils/currency";
import type { SeatOption } from "./seatOptions";
import { useSeatChoiceText } from "./useSeatChoiceText";
import { useSeatFormat } from "./useSeatFormat";

/** Един ред: резервираните маси → сметката, в която отиват. */
export interface SeatOutcomeRow {
  tables: string[];
  option: SeatOption;
  // На кого се отваря новата маса (null — на мен).
  owner?: string | null;
}

/**
 * „Какво ще стане" — резервираните маси → сметката им, с думите на старите
 * карти: „Маси 2, 3 → ЩЕ СЕ ОТВОРИ · Нова маса 2 на Иван" или „Маса 3 →
 * ОТВОРЕНА · Маса 3 на Мария · сумата". „Една сметка" е един ред за всички
 * маси, „Отделна…" — ред за всяка маса. `note` — вместо редовете (няма
 * избрана цел или „Отделна…" е спряна).
 */
export const SeatOutcome = ({ rows, note }: { rows: SeatOutcomeRow[]; note: string | null }) => {
  const text = useSeatChoiceText();
  const format = useSeatFormat();
  if (note !== null) {
    return (
      <p className="m-0 rounded-xl border border-dashed border-pos-line3 px-4 py-3 text-[13px] font-medium leading-[1.4] text-pos-soft2">
        {note}
      </p>
    );
  }
  return (
    <ul className="m-0 list-none overflow-hidden rounded-xl border border-pos-line2 bg-pos-card p-0">
      {rows.map(({ tables, option, owner }) => {
        const card = text.target(option, owner);
        const isNew = option.kind === "new";
        return (
          <li
            key={tables.join(",")}
            className="flex items-center gap-3 border-t border-pos-line2 px-4 py-2.5 first:border-t-0"
          >
            <span className="w-[100px] shrink-0 break-words text-[13px] font-semibold leading-[1.3] text-pos-ink2">
              {format.tables(tables)}
            </span>
            {/* `!` — `.anticon { color: inherit }` на antd е извън Tailwind layer-ите. */}
            <ArrowRightOutlined className="shrink-0 text-[11px] !text-pos-accent" />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span
                className={`text-[10.5px] font-bold uppercase tracking-[1.2px] ${
                  isNew ? "text-pos-accent-ink" : "text-pos-muted"
                }`}
              >
                {card.label}
              </span>
              <span className="break-words text-[14px] font-bold leading-[1.3] text-pos-ink">
                {card.title} <span className="font-medium text-pos-soft2">{card.waiter}</span>
              </span>
            </span>
            {!isNew && (
              <span className="shrink-0 font-pos-mono text-[13.5px] font-bold text-pos-ink">
                {fmtEuro(option.bill.totalAmount)}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
};
